import { useEffect, useState } from "react";
import EmailInvite from "@assets/svg/EmailInvite";
import DeleteIcon from "@assets/svg/DeleteIcon";
import dayjs from "@lib/dayjs";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { useSelector } from "react-redux";
import { isNullOrEmpty } from "@utils/helperFunction";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import CheckMark from "@assets/svg/CheckMark";
import CrossMark from "@assets/svg/CrossMark";
// @ts-ignore
import userImg from "@assets/images/emptyUser.png";
import { useSnackbar } from "notistack";
import { colors } from "@nextui-org/theme";
import {
  addNewReferral,
  getReferralRequests,
} from "@endpoints/candidate.service";
import { referralRequestActionApi } from "@endpoints/referral.service";
import { RootState } from "@store/reducers";
import LoaderInvitations from "./LoaderInvitations";
import CopyIcon from "@assets/svg/CopyIcon";

const Invitations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state: RootState) => state.auth);

  const [referralRequestData, setReferralRequestData] = useState([]);
  const [selectedReferralId, setSelectedReferralId] = useState(null);
  const [isCancelInviteModal, setIsCancelInviteModal] = useState(false);
  const [isInviteModal, setIsInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(undefined);
  const [isInvitationsLoading, setIsInvitationsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"pending" | "accepted">(
    "accepted",
  );

  const getAllReferralRequests = async () => {
    try {
      setIsInvitationsLoading(true);
      const { data } = await getReferralRequests();
      setReferralRequestData(data);
    } catch (error) {
      enqueueSnackbar("Oops! Something went wrong!", { variant: "error" });
    } finally {
      setIsInvitationsLoading(false);
    }
  };

  const notificationRequestAction = async ({ requestId, action }) => {
    setIsLoading({ _id: requestId, action });
    try {
      const data = await referralRequestActionApi({ requestId, action });
      await getAllReferralRequests();
    } catch (e) {
      console.error("Action failed:", e);
    } finally {
      setIsLoading(undefined);
    }
  };

  const resendNewReferral = async (params) => {
    try {
      const { success } = await addNewReferral(params);
      if (success) {
        enqueueSnackbar("Invited sucessfully.", {
          variant: "success",
        });
        setIsInviteModal(true);
        await getAllReferralRequests();
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
    }
  };

  useEffect(() => {
    getAllReferralRequests();
  }, []);

  useEffect(() => {
    const handleInviteSent = () => {
      getAllReferralRequests();
    };
    window.addEventListener("referral-invite:sent", handleInviteSent);
    return () => {
      window.removeEventListener("referral-invite:sent", handleInviteSent);
    };
  }, []);

  const filteredInvites = referralRequestData
    .filter((invite) => {
      if (statusFilter === "pending") return invite?.status === "pending";
      return invite?.status === "approved";
    })
    .sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });

  return (
    <div className="flex-1 px-5 w-full rounded-lg overflow-y-auto no-scrollbar">
      <div className="mb-3">
        <div className="inline-flex items-center rounded-full border border-primary bg-primary p-1">
          <button
            type="button"
            onClick={() => setStatusFilter("pending")}
            className={`text-xs font-semibold px-5 py-1.5 rounded-full transition-colors ${
              statusFilter === "pending"
                ? "bg-white text-charcoal"
                : "bg-transparent text-white"
            }`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("accepted")}
            className={`text-xs font-semibold px-5 py-1.5 rounded-full transition-colors ${
              statusFilter === "accepted"
                ? "bg-white text-charcoal"
                : "bg-transparent text-white"
            }`}
          >
            Accepted
          </button>
        </div>
      </div>
      {isInvitationsLoading ? (
        <LoaderInvitations />
      ) : isNullOrEmpty(filteredInvites) ? (
        <div className="flex flex-col gap-6 items-center mt-40">
          <p className="text-xs font-medium text-charcoal">
            You don’t have any referral member added to your account.
          </p>
        </div>
      ) : (
        <>
          {filteredInvites.map((invite, index) => (
            <div
              key={index}
              className="flex flex-row items-center justify-between border-b py-3.5"
            >
              <div className="flex-1">
                <p
                  className={`text-xs mb-2 font-medium ${
                    invite?.status === "approved"
                      ? "text-[#23BD33]"
                      : invite.status === "declined"
                      ? "text-[#FF3B3B]"
                      : "text-[#FF9800]"
                  }`}
                >
                  {invite?.status === "approved"
                    ? "Accepted"
                    : invite?.status === "declined"
                    ? "Rejected"
                    : "Pending confirmation"}
                </p>

                <p className="text-xs mb-2 text-light-black">
                  {dayjs(invite?.createdAt).format("DD/MM/YY hh:mm")}
                </p>
                <p className="text-xs mb-2 text-charcoal font-semibold">
                  {invite?.external === false ? "Internal" : "External"} invite{" "}
                  {invite.createdBy === user?._id ? "sent" : "received"}
                </p>
              </div>

              <div className="flex flex-1 gap-8">
                <div className="flex flex-col items-center gap-1.5">
                  {invite?.external === true ? (
                    <img
                      src={userImg}
                      className="rounded-full h-16 w-16 object-cover"
                      alt="profileImg"
                    />
                  ) : invite?.candidateId?.profilePic ? (
                    <img
                      src={invite?.candidateId?.profilePic}
                      alt={invite?.candidateId?.name}
                      className="rounded-full h-16 w-16 object-cover"
                    />
                  ) : (
                    <div className="flex rounded-full h-16 w-16 bg-[#E1E0E0] items-center justify-center">
                      <span className="font-montserrat text-white font-semibold text-2xl">
                        {invite?.candidateId?.firstName
                          ?.charAt(0)
                          .toUpperCase()}
                        {invite?.candidateId?.lastName?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}

                  <p className="font-medium text-xs mt-2">
                    {invite?.candidateId?.refNo || "-"}
                  </p>
                </div>

                <div className="flex-1">
                  <p className="text-xs font-medium text-light-black mb-1">
                    {invite?.external === false ? (
                      <>
                        {invite?.candidateId?.firstName}{" "}
                        {invite?.candidateId?.lastName}
                      </>
                    ) : (
                      <>{invite?.candidateName}</>
                    )}
                  </p>
                  {invite?.candidateId?.userId?.userName && (
                    <p className="text-xs text-charcoal">
                      <span className="font-semibold text-xs pe-3">
                        Username :
                      </span>
                      {invite?.candidateId?.userId?.userName || "-"}
                    </p>
                  )}

                  <a className="text-xs text-light-black font-medium break-all">
                    <span className="font-semibold text-xs pe-3 text-charcoal">
                      Email :
                    </span>
                    {invite?.external === false ? (
                      <>{invite?.candidateId?.userId?.email}</>
                    ) : (
                      <>{invite?.candidateEmail}</>
                    )}
                  </a>

                  {invite?.candidateId?.userId?.userName ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://comtracked/profile/${invite.candidateId.userId.userName}`}
                        className="text-xs text-charcoal break-all lowercase line-clamp-1"
                      >
                        {invite?.candidateId?.userId?.userName
                          ? `https://comtracked/profile/${invite.candidateId.userId.userName}`
                          : "-"}
                      </a>
                      <CopyToClipboard
                        text={`https://comtracked/profile/${invite.candidateId.userId.userName}`}
                        onCopy={() =>
                          enqueueSnackbar("Link copied successfully.", {
                            variant: "success",
                          })
                        }
                      >
                        <button className="h-6 w-6 rounded-sm hover:bg-gray-100 flex items-center justify-center">
                          <CopyIcon className={"w-3 h-3"} fill="gray" />
                        </button>
                      </CopyToClipboard>
                    </div>
                  ) : null}
                  {invite?.candidateId?.username && (
                    <a className="text-xs text-light-black font-medium break-all">
                      {invite?.candidateId?.username}
                    </a>
                  )}
                </div>
              </div>

              <div
                className={
                  invite.status === "declined"
                    ? "invisible flex flex-1"
                    : "flex gap-2 flex-1 justify-end "
                }
              >
                {invite.status === "pending" &&
                invite.createdBy !== user?._id ? (
                  <>
                    <button
                      className={
                        "bg-light-white-700 border border-[#ECECEC] rounded-sm p-2 min-w-4"
                      }
                      disabled={
                        !isNullOrEmpty(isLoading) &&
                        isLoading?._id === invite?._id
                      }
                      title="Accept Request"
                      onClick={(e) => {
                        e.stopPropagation();
                        notificationRequestAction({
                          requestId: invite?._id,
                          action: "approved",
                        });
                      }}
                    >
                      {!isNullOrEmpty(isLoading) &&
                      isLoading?._id === invite?._id &&
                      isLoading?.action === "approved" ? (
                        <LoadingAnimation
                          className="h-5 w-5 ml-0 mr-0"
                          color={colors.black}
                        />
                      ) : (
                        <CheckMark width={18} height={16} />
                      )}
                    </button>
                    <button
                      className={
                        "bg-light-white-700 border items-center justify-center border-[#ECECEC] rounded-sm p-2 min-w-4"
                      }
                      title="Reject Request"
                      disabled={
                        !isNullOrEmpty(isLoading) &&
                        isLoading?._id === invite?._id
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        notificationRequestAction({
                          requestId: invite?._id,
                          action: "declined",
                        });
                      }}
                    >
                      {!isNullOrEmpty(isLoading) &&
                      isLoading?._id === invite?._id &&
                      isLoading?.action === "declined" ? (
                        <LoadingAnimation
                          className="h-5 w-5 ml-0 mr-0"
                          color={colors.black}
                        />
                      ) : (
                        <CrossMark
                          className={""}
                          width={16}
                          height={16}
                          fill={"#ffffff"}
                        />
                      )}
                    </button>
                  </>
                ) : invite?.status !== "approved" &&
                  invite?.status !== "declined" ? (
                  <>
                    <button
                      className="bg-light-white-700 border border-[#ECECEC] rounded-sm p-2 opacity-100"
                      title="Resend"
                      onClick={() =>
                        resendNewReferral({
                          requestId: invite?._id,
                        })
                      }
                    >
                      <EmailInvite width={18} height={16} fill={"#ffffff"} />
                    </button>

                    <button
                      className="bg-light-white-700 border border-[#ECECEC] rounded-sm p-2 opacity-100"
                      title="Delete"
                      onClick={() => {
                        setSelectedReferralId(invite?._id);
                        setIsCancelInviteModal(true);
                      }}
                    >
                      {/* @ts-ignore */}
                      <DeleteIcon width={18} height={16} />
                    </button>
                  </>
                ) : (
                  <p className="text-xs px-2 text-gray-600">
                    {dayjs(invite.updatedAt).format("DD-MM-YYYY hh:mm A")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Invitations;
